import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import SlashCommandMenu from '../components/SlashCommandMenu';
import './HaloMobilePage.css';
import AppIcon from '../components/icons/AppIcon';

const LOGO_URL = '/assets/images/logo-halo-fundo-trasparente.png';

// ============================================================================
// UTILITÁRIOS
// ============================================================================

function formatTime(dateStr) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/\{\{AUDIO_RECORDER:[^}]+\}\}/g, '')
    .replace(/\{\{IMAGE_UPLOADER:[^}]+\}\}/g, '')
    .replace(/\{\{PRODUCT_IMAGE_UPLOADER:[^}]+\}\}/g, '')
    .replace(/\{\{QRCODE_IMAGE:[^}]+\}\}/g, '')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n- /g, '\n• ')
    .replace(/\n(\d+)\. /g, '\n$1. ');
  return html;
}

function extractMediaMarkers(text) {
  if (!text) return [];
  const markers = [];
  const qrcodeRegex = /\{\{QRCODE_IMAGE:([^}]+)\}\}/g;
  let match;
  while ((match = qrcodeRegex.exec(text)) !== null) {
    markers.push({ type: 'qrcode', connectionId: match[1] });
  }
  return markers;
}

// ============================================================================
// COMPONENTE DE LOGIN MOBILE
// ============================================================================

function MobileLogin({ onLoginSuccess, error, setError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallTip, setShowInstallTip] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    const installedHandler = () => { setDeferredPrompt(null); setIsInstalled(true); };
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setIsInstalled(true);
    } else {
      setShowInstallTip(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Preencha todos os campos.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });

      if (authError) {
        if (authError.message.includes('Invalid login')) {
          setError('E-mail ou senha incorretos.');
        } else {
          setError(authError.message);
        }
        return;
      }

      if (data?.user) {
        onLoginSuccess(data.user);
      }
    } catch (err) {
      setError('Erro ao conectar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="halo-mobile-login">
      <div className="halo-mobile-login-container">
        <div className="halo-mobile-login-logo">
          <img src={LOGO_URL} alt="Halo" />
        </div>
        <h1 className="halo-mobile-login-title">Halo</h1>
        <p className="halo-mobile-login-subtitle">Assistente Inteligente</p>

        <form onSubmit={handleSubmit} className="halo-mobile-login-form">
          {error && (
            <div className="halo-mobile-login-error">
              <AppIcon name="error" />
              {error}
            </div>
          )}

          <div className="halo-mobile-input-group">
            <AppIcon name="mail" className="halo-mobile-input-icon" />
            <input
              type="email"
              placeholder="Seu e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoCapitalize="none"
              disabled={loading}
            />
          </div>

          <div className="halo-mobile-input-group">
            <AppIcon name="lock" className="halo-mobile-input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
            />
            <button
              type="button"
              className="halo-mobile-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              <AppIcon name={showPassword ? 'visibility_off' : 'visibility'} />
            </button>
          </div>

          <button
            type="submit"
            className="halo-mobile-login-btn"
            disabled={loading}
          >
            {loading ? (
              <div className="halo-mobile-spinner" />
            ) : (
              <>
                <AppIcon name="login" />
                Entrar
              </>
            )}
          </button>
        </form>

        <p className="halo-mobile-login-footer">
          Acesse com suas credenciais da empresa
        </p>

        {!isInstalled && (
          <>
            <button
              type="button"
              className="halo-mobile-login-install-btn"
              onClick={handleInstallPWA}
            >
              <div className="halo-mobile-login-install-icon">
                <img src="/assets/images/icon-192.png" alt="Halo" />
              </div>
              <div className="halo-mobile-login-install-text">
                <span>Adicionar à Tela Inicial</span>
                <small>Acesso rápido ao Halo IA</small>
              </div>
              <AppIcon name="download" className="halo-mobile-login-install-arrow" />
            </button>

            {showInstallTip && (
              <div className="halo-mobile-install-tip">
                <AppIcon name="info" />
                <p>Toque no menu <strong>⋮</strong> do navegador e selecione <strong>"Adicionar à tela inicial"</strong> ou <strong>"Instalar aplicativo"</strong></p>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}

// ============================================================================
// COMPONENTE DE CHAT MOBILE
// ============================================================================

function MobileChat({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState('');
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallTip, setShowInstallTip] = useState(false);
  const [qrCodes, setQrCodes] = useState({});

  // Buscar QR code do banco quando marcador é encontrado
  const fetchQrCode = useCallback(async (connectionId) => {
    if (qrCodes[connectionId]) return;
    try {
      setQrCodes(prev => ({ ...prev, [connectionId]: { loading: true } }));
      const { data, error } = await supabase
        .from('conexoes_uazapi')
        .select('qrcode_dataurl')
        .eq('connection_id', connectionId)
        .single();
      if (error || !data?.qrcode_dataurl) {
        setQrCodes(prev => ({ ...prev, [connectionId]: { loading: false, error: true } }));
        return;
      }
      setQrCodes(prev => ({ ...prev, [connectionId]: { loading: false, dataUrl: data.qrcode_dataurl } }));
    } catch (e) {
      console.error('[fetchQrCode] Erro:', e);
      setQrCodes(prev => ({ ...prev, [connectionId]: { loading: false, error: true } }));
    }
  }, [qrCodes]);

  // Capturar evento beforeinstallprompt para PWA install
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => { setDeferredPrompt(null); setIsInstalled(true); };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstallPWA = async () => {
    setShowMenu(false);
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setIsInstalled(true);
    } else {
      setShowInstallTip(true);
      setTimeout(() => setShowInstallTip(false), 6000);
    }
  };

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Inicializar sessão
  useEffect(() => {
    const companyId = user?.user_metadata?.company_id || user?.app_metadata?.company_id;
    if (!companyId) {
      // Buscar company_id do localStorage
      try {
        const stored = localStorage.getItem('astro_user_data');
        if (stored) {
          const data = JSON.parse(stored);
          initSession(data.company_id);
          setUserName(data.nome || data.email || 'Usuário');
          return;
        }
      } catch {}
    }

    // Buscar dados do perfil
    const loadProfile = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, nome_usuario, company_id')
          .eq('id', user.id)
          .single();

        if (profile) {
          setUserName(profile.nome_usuario || user.email || 'Usuário');
          const cid = profile.company_id || companyId;
          if (cid) {
            initSession(cid);
            // Salvar no localStorage para próximas vezes
            localStorage.setItem('astro_user_data', JSON.stringify({
              id: user.id,
              nome: profile.nome_usuario,
              email: user.email,
              company_id: cid
            }));
          }
        }
      } catch (err) {
        console.error('[HaloMobile] Erro ao carregar perfil:', err);
        setUserName(user.email || 'Usuário');
      }
    };

    loadProfile();
  }, [user]);

  const initSession = (companyId) => {
    const storageKey = `halo_mobile_session_${companyId}`;
    let sid = sessionStorage.getItem(storageKey);
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem(storageKey, sid);
    }
    setSessionId(sid);
  };

  // Carregar histórico quando sessão estiver pronta
  useEffect(() => {
    if (!sessionId) return;
    loadHistory();
  }, [sessionId]);

  // Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus no textarea ao montar
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 300);
  }, []);

  async function getToken() {
    try {
      const { data } = await supabase.auth.getSession();
      return data?.session?.access_token || null;
    } catch {
      return null;
    }
  }

  async function loadHistory() {
    try {
      const token = await getToken();
      if (!token) return;
      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/history/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.messages?.length > 0) {
          setMessages(data.messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.created_at
          })));
        }
      }
    } catch (err) {
      console.error('[HaloMobile] Erro ao carregar histórico:', err);
    }
  }

  // Carregar lista de sessões
  async function loadSessions() {
    setSessionsLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('[HaloMobile] Erro ao carregar sessões:', err);
    } finally {
      setSessionsLoading(false);
    }
  }

  const sendMessage = useCallback(async () => {
    const msg = input.trim();
    if (!msg || isLoading) return;

    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setIsLoading(true);

    try {
      const token = await getToken();
      if (!token) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sessão expirada. Faça login novamente.',
          timestamp: new Date().toISOString()
        }]);
        setIsLoading(false);
        return;
      }

      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: msg, session_id: sessionId })
      });

      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      }]);
    } catch (err) {
      console.error('[HaloMobile] Erro:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro. Tente novamente.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [input, isLoading, sessionId]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';

    // Detectar / para abrir menu
    if (val.startsWith('/')) {
      const filter = val.slice(1).trim();
      setSlashFilter(filter);
      if (!showSlashMenu) setShowSlashMenu(true);
    } else if (showSlashMenu && !val.startsWith('/')) {
      setShowSlashMenu(false);
      setSlashFilter('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (showSlashMenu) {
        setShowSlashMenu(false);
        setSlashFilter('');
      }
      sendMessage();
    }
    if (e.key === 'Escape' && showSlashMenu) {
      setShowSlashMenu(false);
      setSlashFilter('');
    }
  };

  const handleNewSession = () => {
    const companyId = (() => {
      try {
        const stored = localStorage.getItem('astro_user_data');
        if (stored) return JSON.parse(stored).company_id;
      } catch {}
      return user?.user_metadata?.company_id || 'default';
    })();

    const storageKey = `halo_mobile_session_${companyId}`;
    const newSid = crypto.randomUUID();
    sessionStorage.setItem(storageKey, newSid);
    setSessionId(newSid);
    setMessages([]);
    setShowMenu(false);
    setShowSessions(false);
  };

  const switchSession = (sid) => {
    const companyId = (() => {
      try {
        const stored = localStorage.getItem('astro_user_data');
        if (stored) return JSON.parse(stored).company_id;
      } catch {}
      return user?.user_metadata?.company_id || 'default';
    })();

    const storageKey = `halo_mobile_session_${companyId}`;
    sessionStorage.setItem(storageKey, sid);
    setSessionId(sid);
    setMessages([]);
    setShowSessions(false);
    setShowMenu(false);
  };

  const userInitial = userName ? userName.charAt(0).toUpperCase() : 'U';

  return (
    <div className="halo-mobile-chat">
      {/* Header */}
      <div className="halo-mobile-header">
        <div className="halo-mobile-header-left">
          <div className="halo-mobile-header-avatar">
            <img src={LOGO_URL} alt="Halo" />
          </div>
          <div className="halo-mobile-header-info">
            <div className="halo-mobile-header-name">Halo</div>
            <div className="halo-mobile-header-status">Assistente IA</div>
          </div>
        </div>
        <div className="halo-mobile-header-actions">
          <button
            className="halo-mobile-header-btn"
            onClick={handleNewSession}
            title="Nova conversa"
          >
            <AppIcon name="add_comment" />
          </button>
          <button
            className="halo-mobile-header-btn"
            onClick={() => {
              if (!showSessions) loadSessions();
              setShowSessions(!showSessions);
              setShowMenu(false);
            }}
            title="Histórico"
          >
            <AppIcon name="history" />
          </button>
          <button
            className="halo-mobile-header-btn"
            onClick={() => { setShowMenu(!showMenu); setShowSessions(false); }}
            title="Menu"
          >
            <AppIcon name="more_vert" />
          </button>
        </div>

        {/* Dropdown Menu */}
        {showMenu && (
          <div className="halo-mobile-dropdown">
            {!isInstalled && (
              <button onClick={handleInstallPWA} className="halo-mobile-dropdown-install">
                <div className="halo-mobile-dropdown-install-icon">
                  <img src={LOGO_URL} alt="Halo" />
                </div>
                <div className="halo-mobile-dropdown-install-text">
                  <span>Adicionar à Tela Inicial</span>
                  <small>Halo IA</small>
                </div>
              </button>
            )}
            <button onClick={() => { onLogout(); setShowMenu(false); }}>
              <AppIcon name="logout" />
              Sair
            </button>
          </div>
        )}

        {/* Dica de instalação */}
        {showInstallTip && (
          <div className="halo-mobile-install-tip">
            <AppIcon name="info" />
            <p>Toque no menu <strong>⋮</strong> do navegador e selecione <strong>"Adicionar à tela inicial"</strong> ou <strong>"Instalar aplicativo"</strong></p>
            <button onClick={() => setShowInstallTip(false)}>
              <AppIcon name="close" />
            </button>
          </div>
        )}

        {/* Painel de Sessões */}
        {showSessions && (
          <div className="halo-mobile-sessions-panel">
            <div className="halo-mobile-sessions-header">
              <h3>Conversas</h3>
              <button onClick={() => setShowSessions(false)}>
                <AppIcon name="close" />
              </button>
            </div>
            <div className="halo-mobile-sessions-list">
              {sessionsLoading ? (
                <div className="halo-mobile-sessions-loading">Carregando...</div>
              ) : sessions.length === 0 ? (
                <div className="halo-mobile-sessions-empty">Nenhuma conversa anterior</div>
              ) : (
                sessions.map((s, i) => (
                  <button
                    key={s.session_id || i}
                    className={`halo-mobile-session-item ${s.session_id === sessionId ? 'active' : ''}`}
                    onClick={() => switchSession(s.session_id)}
                  >
                    <AppIcon name="chat_bubble" />
                    <div className="halo-mobile-session-info">
                      <div className="halo-mobile-session-preview">
                        {s.last_message || 'Conversa sem mensagens'}
                      </div>
                      <div className="halo-mobile-session-date">
                        {s.updated_at ? new Date(s.updated_at).toLocaleDateString('pt-BR') : ''}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mensagens */}
      <div className="halo-mobile-messages">
        {messages.length === 0 && !isLoading && (
          <div className="halo-mobile-welcome">
            <div className="halo-mobile-welcome-icon">
              <img src={LOGO_URL} alt="Halo" />
            </div>
            <h2>Olá, {userName}!</h2>
            <p>
              Sou o Halo, seu assistente inteligente. Posso ajudar com consultas,
              gerenciar contatos, projetos, agendamentos e muito mais.
            </p>
            <p className="halo-mobile-welcome-tip">
              <AppIcon name="lightbulb" />
              Digite <strong>/</strong> para ver todas as ferramentas disponíveis
            </p>
          </div>
        )}

        {messages.map((msg, i) => {
          const mediaMarkers = msg.role === 'assistant' ? extractMediaMarkers(msg.content) : [];
          return (
            <div key={i} className={`halo-mobile-message ${msg.role}`}>
              <div className="halo-mobile-msg-avatar">
                {msg.role === 'assistant' ? (
                  <img src={LOGO_URL} alt="Halo" />
                ) : (
                  userInitial
                )}
              </div>
              <div className="halo-mobile-msg-content">
                <div
                  className="halo-mobile-msg-bubble"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(msg.role === 'assistant'
                      ? renderMarkdown(msg.content)
                      : msg.content)
                  }}
                />
                {/* Widgets inline de mídia */}
                {mediaMarkers.map((marker, mi) => {
                  if (marker.type === 'qrcode') {
                    const connId = marker.connectionId;
                    const qrState = qrCodes[connId];
                    if (!qrState) {
                      fetchQrCode(connId);
                    }
                    return (
                      <div key={mi} className="halo-mobile-media-widget">
                        <div className="halo-mobile-media-header">
                          <AppIcon name="qr_code_2" />
                          <span>QR Code WhatsApp</span>
                        </div>
                        <div className="halo-mobile-media-body">
                          {qrState?.loading ? (
                            <div className="halo-mobile-media-loading">
                              <AppIcon name="progress_activity" className="halo-spin" />
                              <span>Carregando QR Code...</span>
                            </div>
                          ) : qrState?.dataUrl ? (
                            <img src={qrState.dataUrl} alt="QR Code WhatsApp" className="halo-mobile-qrcode-img" />
                          ) : (
                            <div className="halo-mobile-media-loading">
                              <AppIcon name="qr_code_2" />
                              <span>{qrState?.error ? 'Não foi possível carregar o QR Code' : 'Escaneie o QR Code com o WhatsApp'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
                <div className="halo-mobile-msg-time">{formatTime(msg.timestamp)}</div>
              </div>
            </div>
          );
        })}

        {isLoading && (
          <div className="halo-mobile-typing">
            <div className="halo-mobile-msg-avatar">
              <img src={LOGO_URL} alt="Halo" />
            </div>
            <div className="halo-mobile-typing-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="halo-mobile-input-area">
        <SlashCommandMenu
          isOpen={showSlashMenu}
          onClose={() => { setShowSlashMenu(false); setSlashFilter(''); }}
          onSelectCommand={(command) => {
            setInput(`/${command} `);
            setShowSlashMenu(false);
            setSlashFilter('');
            setTimeout(() => textareaRef.current?.focus(), 50);
          }}
          filterText={slashFilter}
        />

        <button
          className={`slash-menu-trigger ${showSlashMenu ? 'active' : ''}`}
          onClick={() => {
            setShowSlashMenu(prev => !prev);
            setSlashFilter('');
          }}
          title="Ferramentas"
          type="button"
        >
          <AppIcon name={showSlashMenu ? 'close' : 'apps'} />
        </button>

        <textarea
          ref={textareaRef}
          className="halo-mobile-input"
          placeholder="Mensagem para o Halo..."
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={isLoading}
        />

        <button
          className="halo-mobile-send-btn"
          onClick={() => {
            if (showSlashMenu) {
              setShowSlashMenu(false);
              setSlashFilter('');
            }
            sendMessage();
          }}
          disabled={!input.trim() || isLoading}
          title="Enviar"
        >
          <AppIcon name="send" />
        </button>
      </div>

    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL - HALO MOBILE PAGE
// ============================================================================

const HALO_MOBILE_AUTH_KEY = 'halo_mobile_auth';

export default function HaloMobilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Verificar sessão PRÓPRIA do /halo (separada do painel principal)
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Verificar se o /halo tem login próprio salvo
        const haloAuth = localStorage.getItem(HALO_MOBILE_AUTH_KEY);
        if (haloAuth) {
          const saved = JSON.parse(haloAuth);
          // Verificar se a sessão Supabase ainda é válida
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user && session.user.id === saved.userId) {
            setUser(session.user);
          } else {
            // Sessão expirou, limpar auth do /halo
            localStorage.removeItem(HALO_MOBILE_AUTH_KEY);
          }
        }
        // Se não tem haloAuth, NÃO usar sessão do painel — forçar login
      } catch (err) {
        console.error('[HaloMobile] Erro ao verificar sessão:', err);
        localStorage.removeItem(HALO_MOBILE_AUTH_KEY);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // Callback de login: salvar auth próprio do /halo
  const handleLoginSuccess = (loggedUser) => {
    localStorage.setItem(HALO_MOBILE_AUTH_KEY, JSON.stringify({
      userId: loggedUser.id,
      email: loggedUser.email,
      loggedAt: new Date().toISOString()
    }));
    setUser(loggedUser);
  };

  const handleLogout = async () => {
    try {
      // Limpar auth próprio do /halo
      localStorage.removeItem(HALO_MOBILE_AUTH_KEY);
      // NÃO fazer supabase.auth.signOut() para não deslogar do painel principal
      setUser(null);
      setError('');
    } catch (err) {
      console.error('[HaloMobile] Erro ao fazer logout:', err);
    }
  };

  if (loading) {
    return (
      <div className="halo-mobile-loading">
        <div className="halo-mobile-loading-logo">
          <img src={LOGO_URL} alt="Halo" />
        </div>
        <div className="halo-mobile-spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <MobileLogin
        onLoginSuccess={handleLoginSuccess}
        error={error}
        setError={setError}
      />
    );
  }

  return <MobileChat user={user} onLogout={handleLogout} />;
}
